import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react'

import {
  makeStyles,
  Collapse,
  Grid,
  Skeleton,
  useIsMobile,
  useProcessEvent,
  useClientOptions,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DropdownSelect,
  EstimatorSelect,
  EventsMap,
  IconButton,
  SelectableChipSmall,
  SingleDatePickerController,
  Spinner,
  Typography,
  useCrmDialogs,
  useDatePickerStyles,
  useUsers
} from '@paintscout/ui'
import type { MuiTheme as Theme, DialogProps, DropdownSelectOption } from '@paintscout/ui'
import CloseIcon from '@material-ui/icons/Close'
import type { EventActivity, SearchActivitiesQuery, User } from '@paintscout/api'
import { useSearchActivitiesQuery } from '@paintscout/api'
import { addAuth0Prefix, getEstimatorDayAvailability, removeAuth0Prefix } from '@paintscout/util'
import type { DashboardEstimator } from '@paintscout/util'
import { formatName, mapActivitiesToCalendarEvents, getTimezone } from '@paintscout/util/builder'
import classnames from 'classnames'
import { jsonToLucene } from 'json-to-lucene'
import times from 'lodash/times'
import type { Moment } from 'moment'
import moment from 'moment-timezone'
import type { CalendarEvent, EventType } from 'paintscout'

const DATE_FORMAT = 'YYYY-MM-DD'

const useStyles = makeStyles<Theme, ScheduleEventDialogProps & { tableHeight?: number }>((theme) => ({
  overflowVisible: {
    overflow: 'visible'
  },
  closeButton: {
    color: theme.palette.text.primary
  },
  availability: {
    maxHeight: ({ tableHeight }) => (tableHeight ? `${tableHeight - 72}px` : '400px'),
    overflowY: 'auto',
    overflowX: 'hidden',
    [theme.breakpoints.down('sm')]: {
      maxHeight: '300px'
    }
  },
  estimatorAvailability: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(3),
    marginLeft: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0
    }
  },
  availabilityChips: {
    display: 'flex',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    flexWrap: 'wrap'
  },
  datePicker: {
    padding: '0px !important',
    boxShadow: 'none !important',
    width: '350px',
    overflow: 'hidden',
    zIndex: 1000,
    marginTop: theme.spacing(-1),
    [theme.breakpoints.down('sm')]: {
      marginTop: 0
    }
  },
  dayEvents: {
    marginTop: theme.spacing(2),
    zIndex: 1001,
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(0)
    }
  },
  dayEvent: {
    backgroundColor: theme.paintscout.palette.brand.Neutral.P96,
    padding: theme.spacing(1.2),
    borderRadius: theme.borderRadius.md,
    marginTop: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.8)
    }
  },
  scheduledLabel: {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    width: '100%',
    display: 'inline-block',
    marginBottom: '-6px'
  },
  aboveDatePicker: {
    zIndex: 1001
  },
  spinner: {
    width: '100%',
    height: '500px',
    maxHeight: '100%'
  },
  mapWrapper: {
    marginTop: theme.spacing(2),
    '& > div:first-child': {
      maxHeight: '600px'
    }
  }
}))

export interface ScheduleEventDialogProps extends DialogProps {
  event: Partial<CalendarEvent>
  date?: Date
  selectedUsers?: User[]
  isNew?: boolean
  onConfirm: (event: CalendarEvent) => void
  onClose?: () => void
}

export default function ScheduleEventDialog(props: ScheduleEventDialogProps) {
  const { dismissAllDialogs, dismissDialog } = useCrmDialogs()
  const { event, onConfirm, onClose = dismissAllDialogs, selectedUsers, isNew, ...otherProps } = props
  const { users, loading: usersLoading } = useUsers()
  const { options } = useClientOptions()
  const timezone = getTimezone({ options })
  const { processEvent } = useProcessEvent()
  const mobile = useIsMobile()

  const initialDuration = event.start && event.end ? moment(event.end).diff(moment(event.start), 'minutes') : 60
  const [eventDuration, setEventDuration] = useState<number>(initialDuration)
  const hasInitializedFromSelectedUsers = useRef(false)
  const previousSelectedUsersRef = useRef<User[]>([])
  const [viewedEstimators, setViewedEstimators] = useState<DropdownSelectOption[]>(
    event.estimator
      ? [
          {
            label: event.estimator.name,
            value: event.estimator.id,
            color: event.estimator.color,
            email: event.estimator.email
          }
        ]
      : []
  )
  const [selectedEstimator, setSelectedEstimator] = useState<DropdownSelectOption | null>(viewedEstimators?.[0] || null)

  useEffect(() => {
    if (usersLoading || !users?.length) return
    if (!selectedUsers?.length) {
      previousSelectedUsersRef.current = []
      if (!hasInitializedFromSelectedUsers.current && event.estimator) {
        return
      }
      return
    }

    const selectedUsersChanged =
      previousSelectedUsersRef.current.length !== selectedUsers.length ||
      !previousSelectedUsersRef.current.every((prevUser) =>
        selectedUsers.some((user) => user.user_id === prevUser.user_id)
      )

    if (hasInitializedFromSelectedUsers.current && !selectedUsersChanged) return

    const newViewedEstimators = selectedUsers
      .filter((user) => {
        const matchingUser = users.find((u) => u.user_id === user.user_id)
        return (matchingUser?.app_metadata?.roles || []).some((role) => ['admin', 'sales'].includes(role))
      })
      .map((user) => {
        const matchingUser = users.find((u) => u.user_id === user.user_id)
        return {
          label: matchingUser ? formatName(matchingUser.user_metadata || matchingUser) : user.user_id,
          value: matchingUser?.user_id || user.user_id,
          color: matchingUser?.user_metadata?.color || '#000000',
          email: matchingUser?.email || ''
        }
      })

    if (newViewedEstimators.length > 0) {
      setViewedEstimators(newViewedEstimators)
      setSelectedEstimator(newViewedEstimators[0])
      hasInitializedFromSelectedUsers.current = true
      previousSelectedUsersRef.current = selectedUsers
    }
  }, [users, usersLoading, selectedUsers, isNew, event.estimator])
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<Moment>(
    event.start ? moment(event.start).tz(timezone) : null
  )
  const [tableHeight, setTableHeight] = useState(0)
  const [date, setDate] = useState<Moment>(moment(event.start || props.date))

  const [dayEvents, setDayEvents] = useState<CalendarEvent[]>([])
  const [googleDayEvents, setGoogleDayEvents] = useState<CalendarEvent[]>([])
  const [dayEventsLoading, setDayEventsLoading] = useState(false)
  const [googleDayEventsLoading, setGoogleDayEventsLoading] = useState(false)

  async function getGoogleCalendarEvents() {
    if (!viewedEstimators?.length || !date) {
      return
    }
    setGoogleDayEventsLoading(true)
    const { result, error } = await processEvent({
      type: 'get-day-events' as EventType,
      provider: 'google-calendar',
      params: {
        userIds: viewedEstimators.map((estimator) => estimator.value),
        date
      }
    })
    setGoogleDayEventsLoading(false)

    if (error) {
      console.error('Error fetching Google Calendar events:', error)
      return
    }
    if (result) {
      setGoogleDayEvents(mapActivitiesToCalendarEvents(result.result as EventActivity[], users || []))
    }
  }

  useEffect(() => {
    if (viewedEstimators?.length && date) {
      getGoogleCalendarEvents()
    }
  }, [viewedEstimators, date])

  const { refetch } = useSearchActivitiesQuery({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: false,
    variables: {
      query: jsonToLucene({
        filters: [
          {
            type: 'equal',
            id: 'details.activityType',
            value: 'event'
          },
          {
            type: 'equal',
            id: 'details.production',
            value: true,
            not: true
          },
          {
            type: 'range',
            id: 'details.start',
            value: [
              moment.tz(date.format(DATE_FORMAT), DATE_FORMAT, timezone).startOf('day').unix() * 1000,
              moment.tz(date.format(DATE_FORMAT), DATE_FORMAT, timezone).endOf('day').unix() * 1000
            ]
          },
          { type: 'equal', id: 'details.canceled', value: true, not: true },
          {
            type: 'or',
            value: [
              {
                type: 'in',
                id: 'details.owner.id',
                value: viewedEstimators.map((estimator) => addAuth0Prefix(estimator.value))
              },
              {
                type: 'in',
                id: 'details.owner.id',
                value: viewedEstimators.map((estimator) => removeAuth0Prefix(estimator.value))
              }
            ]
          }
        ]
      }),
      skipSanitize: true
    },
    onError: useCallback((error) => {
      console.error(error)
    }, []),
    onCompleted: useCallback((data: SearchActivitiesQuery) => {
      const calendarEvents = mapActivitiesToCalendarEvents(
        data.searchActivities.activities as EventActivity[],
        users || []
      )
      setDayEvents(calendarEvents)
      setTimeout(() => {
        setDayEventsLoading(false)
      }, 100)
    }, [])
  })

  useEffect(() => {
    if (viewedEstimators.length > 0 && date && refetch) {
      setDayEventsLoading(true)
      refetch()
    }
  }, [viewedEstimators, date, refetch])

  useEffect(() => {
    recalculateTableHeight()
    setGoogleDayEvents([]) // Reset Google events when date changes
  }, [date])

  const recalculateTableHeight = () => {
    const elements = document.getElementsByClassName('CalendarMonthGrid_month__horizontal')
    if (elements.length > 0) {
      const height = (elements[0] as any).offsetHeight
      setTableHeight(height)
    }
  }

  const classes = useStyles({ ...props, tableHeight })
  const datePickerClasses = useDatePickerStyles({
    isSingle: true,
    tableHeight: tableHeight ? tableHeight - (mobile ? 40 : 20) : null,
    blockPastDays: true
  })

  const generateIncrementOptions = (maxHours: number): DropdownSelectOption[] => {
    const options: DropdownSelectOption[] = []
    for (let i = 0.5; i <= maxHours; i += 0.5) {
      options.push({
        label: `${i} ${i === 1 ? 'hour' : 'hours'}`,
        value: i * 60
      })
    }
    return options
  }

  const incrementOptions = generateIncrementOptions(8)

  const handleConfirm = () => {
    const momentStart = moment.tz(date.format(DATE_FORMAT), DATE_FORMAT, timezone).set({
      hour: selectedTimeSlot.hour(),
      minute: selectedTimeSlot.minute(),
      second: 0,
      millisecond: 0
    })
    const inferredEstimator = selectedEstimator || viewedEstimators?.[0]

    if (!inferredEstimator) {
      return
    }

    const matchingUser = users.find((u) => removeAuth0Prefix(u.user_id) === removeAuth0Prefix(inferredEstimator.value))

    const estimatorName = matchingUser
      ? formatName(matchingUser.user_metadata || matchingUser)
      : inferredEstimator.label || inferredEstimator.value

    onConfirm({
      ...event,
      start: momentStart.toDate(),
      end: momentStart.clone().add(eventDuration, 'minutes').toDate(),
      estimator: {
        id: inferredEstimator.value,
        name: estimatorName,
        color: matchingUser?.user_metadata?.color || inferredEstimator.color || '#000000',
        email: matchingUser?.email || inferredEstimator.email || ''
      }
    } as CalendarEvent)
  }

  const getEventsToDisplay = () => {
    let eventsToDisplay = [...dayEvents, ...googleDayEvents]
    // Ensure the current event is included if showing map view and it has an address
    const shouldIncludeCurrentEvent = event?.geocode && !dayEvents.some((e) => e.id === event.id)

    if (shouldIncludeCurrentEvent) {
      eventsToDisplay.push({
        ...(event as CalendarEvent),
        estimator: selectedEstimator
          ? {
              id: selectedEstimator.value as string,
              name: selectedEstimator.label,
              color: selectedEstimator.color,
              email: selectedEstimator.email
            }
          : undefined,
        start: selectedTimeSlot ? selectedTimeSlot.toDate() : event.start,
        end: selectedTimeSlot ? selectedTimeSlot.clone().add(eventDuration, 'minutes').toDate() : event.end
      })
    }

    // If map view, update the current event's time to match selection
    if (event?.id) {
      eventsToDisplay = eventsToDisplay.map((e) =>
        e.id === event.id
          ? {
              ...e,
              start: selectedTimeSlot ? selectedTimeSlot.toDate() : event.start,
              end: selectedTimeSlot ? selectedTimeSlot.clone().add(eventDuration, 'minutes').toDate() : event.end
            }
          : e
      )
    }

    // only show events with an address
    eventsToDisplay = eventsToDisplay.filter((e) => e.geocode)

    const uniqueEvents = new Map<string, CalendarEvent>()
    eventsToDisplay.forEach((event) => {
      const key = `${event.start?.toISOString()}-${event.end?.toISOString()}-${event.estimator?.id}`
      if (!uniqueEvents.has(key)) {
        uniqueEvents.set(key, event)
      }
    })

    return Array.from(uniqueEvents.values()).sort((a, b) => {
      const aStart = Number(new Date(a.start))
      const bStart = Number(new Date(b.start))
      return aStart - bStart
    })
  }

  const collapseOpen = viewedEstimators.length > 0 && eventDuration > 0

  const eventsToDisplay = getEventsToDisplay()

  // Whenever the duration changes, we need to reset the selected time slot if it is not available anymore
  useEffect(() => {
    if (usersLoading || !users.length || !selectedTimeSlot) {
      return
    }
    const availableTimeSlots = viewedEstimators
      .map((estimator) => {
        const user = users.find((user) => removeAuth0Prefix(user.user_id) === removeAuth0Prefix(estimator.value))
        if (user) {
          return getEstimatorDayAvailability({
            events: [...dayEvents, ...googleDayEvents],
            estimator: user,
            date,
            eventDuration,
            timezone
          })
        } else {
          return []
        }
      })
      .flat()
    const isTimeSlotAvailable = availableTimeSlots.some((time) => time.isSame(selectedTimeSlot))

    if (!isTimeSlotAvailable) {
      setSelectedTimeSlot(null)
    }
  }, [eventDuration, dayEvents, googleDayEvents])

  const selectedIncrement = incrementOptions.find((option) => option.value === eventDuration)
  const showCalendarAndTimeSlots = viewedEstimators.length > 0 && eventDuration > 0

  return (
    <Dialog {...otherProps} maxWidth="md" fullWidth classes={{ paper: collapseOpen ? null : classes.overflowVisible }}>
      <DialogTitle
        rightContent={
          <IconButton className={classes.closeButton} size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        }
      >
        Schedule
      </DialogTitle>
      <DialogContent className={collapseOpen ? null : classes.overflowVisible}>
        {usersLoading || !users.length ? (
          <Spinner className={classes.spinner} />
        ) : (
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} sm={6}>
              <EstimatorSelect
                roles={['admin', 'sales']}
                className={classes.aboveDatePicker}
                value={viewedEstimators.map((estimator) => estimator.value)}
                onChange={(estimators: DropdownSelectOption[]) => {
                  // Ensure labels are properly set from user data
                  const updatedEstimators = estimators.map((estimator) => {
                    const matchingUser = users.find(
                      (u) => removeAuth0Prefix(u.user_id) === removeAuth0Prefix(estimator.value)
                    )
                    return {
                      ...estimator,
                      label: matchingUser
                        ? formatName(matchingUser.user_metadata || matchingUser)
                        : estimator.label || estimator.value,
                      color: matchingUser?.user_metadata?.color || estimator.color || '#000000',
                      email: matchingUser?.email || estimator.email || ''
                    }
                  })
                  setViewedEstimators(updatedEstimators)
                  setSelectedTimeSlot(null)
                  setSelectedEstimator(
                    updatedEstimators.length === 1
                      ? updatedEstimators[0]
                      : updatedEstimators.find((e) => e.value === selectedEstimator?.value) ||
                          updatedEstimators[0] ||
                          null
                  )
                }}
                userFields={['user_metadata.availability']}
                label="Estimator"
                variant="multi"
                onFetched={(estimatorOptions: DashboardEstimator[]) => {
                  if (estimatorOptions.length === 1) {
                    setSelectedEstimator(estimatorOptions[0])
                    setViewedEstimators(estimatorOptions)
                  } else {
                    return
                  }
                }}
                autoApply
                searchable
                showCollapsibleChips
                autoChipsOpen
                popperModifiers={{
                  flip: {
                    enabled: false
                  },
                  preventOverflow: {
                    enabled: false
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DropdownSelect
                className={classes.aboveDatePicker}
                variant="single"
                label="Duration"
                options={incrementOptions}
                value={selectedIncrement}
                onChange={(option) => {
                  setEventDuration(option.value)
                }}
              />
            </Grid>

            <Grid item xs={12} md={6} className={classnames(datePickerClasses.popper, classes.datePicker)}>
              <Collapse show={showCalendarAndTimeSlots}>
                {' '}
                <SingleDatePickerController
                  date={date}
                  onChange={setDate}
                  recalculateTableHeight={recalculateTableHeight}
                  blockPastDays
                />
              </Collapse>
            </Grid>

            <Grid item xs={12} md={6} className={classnames(classes.availability, 'schedule-event-availability')}>
              <Collapse show={showCalendarAndTimeSlots}>
                {viewedEstimators.map((estimator) => {
                  const user = users.find(
                    (user) => removeAuth0Prefix(user.user_id) === removeAuth0Prefix(estimator.value)
                  )
                  const estimatorName = user
                    ? formatName(user.user_metadata || user)
                    : estimator.label || estimator.value
                  const availabilitySlots = user
                    ? getEstimatorDayAvailability({
                        events: [...dayEvents.filter((eventF) => event.id != eventF.id), ...googleDayEvents],
                        estimator: user,
                        date,
                        eventDuration,
                        timezone
                      })
                    : []
                  return (
                    <div key={estimator.value} className={classes.estimatorAvailability}>
                      <Typography variant="body2" style={{ fontWeight: 500, marginBottom: 8 }}>
                        {viewedEstimators.length > 1 ? (
                          <>
                            <span style={{ fontWeight: 700 }}>{estimatorName}</span>{' '}
                            {availabilitySlots.length ? 'availability' : 'unavailable on this day'}
                          </>
                        ) : availabilitySlots.length ? (
                          'Availability'
                        ) : (
                          'Unavailable on this day'
                        )}
                      </Typography>
                      <div className={classes.availabilityChips}>
                        {dayEventsLoading || googleDayEventsLoading ? (
                          <>
                            {times(12, (i) => (
                              <Skeleton key={i} variant="rect" width="72px" height={32} />
                            ))}
                          </>
                        ) : (
                          <>
                            {availabilitySlots.map((time, index) => (
                              <SelectableChipSmall
                                className={`tour-time-slot-chip`}
                                classes={{ root: `time-slot-chip-${index}` }}
                                key={time.format('h:mma')}
                                label={time.format('h:mma')}
                                selected={
                                  selectedTimeSlot?.isSame(time) &&
                                  (selectedEstimator?.value === estimator.value || viewedEstimators.length === 1)
                                }
                                variant="filled"
                                onClick={() => {
                                  setSelectedEstimator(estimator)
                                  setSelectedTimeSlot(time)
                                }}
                                style={{ width: '72px' }}
                              />
                            ))}
                          </>
                        )}
                      </div>
                    </div>
                  )
                })}
              </Collapse>
            </Grid>

            <Grid item xs={12} className={classnames(classes.dayEvents, 'schedule-event-day-events')}>
              <Collapse show={showCalendarAndTimeSlots && !dayEventsLoading}>
                {eventsToDisplay.length > 0 && date && selectedEstimator && viewedEstimators.length <= 1 && (
                  <Fragment key={selectedEstimator.value}>
                    <Typography className={classes.scheduledLabel} variant="overline" weight={500}>
                      Scheduled On This Day {viewedEstimators.length === 1 ? '' : `(${selectedEstimator?.label})`}
                    </Typography>
                    <div className={classes.mapWrapper}>
                      <EventsMap
                        hideMapTypeButtons
                        events={eventsToDisplay.filter(
                          (event) =>
                            removeAuth0Prefix(event.estimator?.id) === removeAuth0Prefix(selectedEstimator.value)
                        )}
                        editingEventId={event.id}
                        dayView
                      />
                    </div>
                  </Fragment>
                )}
              </Collapse>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions
        leftButton={
          <Button variant="textNoPadding" onClick={dismissDialog}>
            Back
          </Button>
        }
      >
        <Button
          onClick={handleConfirm}
          disabled={!selectedTimeSlot || eventDuration <= 0 || !date || !selectedIncrement}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  )
}
